import { useEffect, useRef } from "react";
import { useAccount, useConnect, useConnectors } from "wagmi";

import { isLedgerConnectorId, isLedgerLiveBrowser } from "@config/ledger";
import { sentryErrorLog } from "@lib/utils";

/**
 * Connects automatically inside Ledger's DApp Browser, where the wallet is
 * already unlocked and a connect prompt is noise.
 *
 * Returning users need nothing from this hook: Ledger reports the selected
 * account through `eth_accounts`, so wagmi's mount reconnect authorises the
 * Ledger connector on its own. This covers the first visit, where the account
 * has not been granted yet and has to be requested.
 */
const useAutoConnect = () => {
  const connectors = useConnectors();
  const { connect } = useConnect({
    mutation: {
      // This only reproduces inside Ledger's DApp Browser, where attaching a
      // debugger is rarely an option, so a failed auto-connect has to be
      // reported rather than swallowed.
      onError: (error) => sentryErrorLog(error),
    },
  });
  const { status } = useAccount();

  // One attempt per page load, so an explicit disconnect is not undone.
  const hasAttemptedRef = useRef(false);

  useEffect(() => {
    if (hasAttemptedRef.current || status !== "disconnected") return;
    if (!isLedgerLiveBrowser()) return;

    // Either Ledger connector will do — the one registered in `@config/ledger`,
    // or the one wagmi discovers over EIP-6963. `useConnectors()` is reactive,
    // so this re-runs if discovery adds the latter.
    const connector = connectors.find((c) => isLedgerConnectorId(c.id));
    if (!connector) return;

    hasAttemptedRef.current = true;
    connect({ connector });
  }, [connect, connectors, status]);
};

export default useAutoConnect;
